import { useMemo } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNext from '@mui/icons-material/NavigateNext';
import { useTranslation } from 'react-i18next';
import { moduleNavigation } from '@/config/navigation.custom';

/**
 * Breadcrumbs for the AppBar showing current module and view.
 * Derives everything from the URL path and navigation config.
 */
export function AppBreadcrumbs() {
  const location = useLocation();
  const { t } = useTranslation();

  const crumbs = useMemo(() => {
    const path = location.pathname;

    for (const mod of moduleNavigation) {
      for (const category of mod.categories) {
        for (const item of category.items) {
          // Match exact path or detail view path (item.path + /:id)
          if (path === item.path || path.startsWith(item.path + '/')) {
            const moduleLabel = mod.labelKey ? t(mod.labelKey, mod.label) : mod.label;
            const viewLabel = item.labelKey ? t(item.labelKey, item.label) : item.label;

            // Find a sensible default path for the module link (first item in first category)
            const firstItem = mod.categories
              .flatMap(c => c.items)
              .sort((a, b) => a.order - b.order)[0];
            const modulePath = firstItem?.path || item.path;

            return { moduleLabel, modulePath, viewLabel, viewPath: item.path };
          }
        }
      }
    }

    return null;
  }, [location.pathname, t]);

  if (!crumbs) {
    return null;
  }

  const linkSx = { color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#fff' } };

  return (
    <Breadcrumbs
      separator={<NavigateNext sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />}
      sx={{ flexGrow: 1 }}
    >
      <Link component={RouterLink} to={crumbs.modulePath} sx={linkSx} variant="body1">
        {crumbs.moduleLabel}
      </Link>
      <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>
        {crumbs.viewLabel}
      </Typography>
    </Breadcrumbs>
  );
}
